import type { OptionsDocument, PresentationBlock, PresentationEstimator, QuoteDocument } from 'paintscout'
import type { PresentationIsEmptyProps } from '../index'

/** One rendered block, paired with where it sits in the **unfiltered** `section.blocks`. */
export interface VisibleBlock {
  block: PresentationBlock
  /**
   * Index in `section.blocks`, NOT in the returned array. Every mutation the canvas performs —
   * `handleAddBlock`, `handleDropBlock`, `BlockMenu`'s duplicate, the formik
   * `pages[i].sections[j].blocks[k]` path — addresses the unfiltered array, so this is the number
   * they all need (#22775).
   */
  blockIndex: number
}

/** The subset of a block's registry entry this filter reads — see `../blocks/index.ts`. */
export interface VisibleBlockComponents {
  [type: string]: { isEmpty?: (props: PresentationIsEmptyProps) => boolean } | undefined
}

export interface GetVisibleBlocksArgs {
  blocks?: PresentationBlock[]
  blockComponents?: VisibleBlockComponents
  quote?: QuoteDocument
  options?: OptionsDocument
  estimator?: PresentationEstimator
  isPrint?: boolean
  isSettings?: boolean
  /**
   * `!isPreview && isSettings` — the editable canvas. It keeps a block whose type has no v2
   * renderer so the author can see and delete it; every other surface drops it.
   *
   * `editable` implies `isSettings`, so the two arms below are mutually exclusive and the editable
   * canvas filters nothing. That is load-bearing: it is what lets `Presentation.tsx` hand react-dnd
   * a rendered position and still address `section.blocks` by the same number. Passing
   * `editable: true` with `isSettings: false` breaks the invariant; the no-renderer arm still wins
   * (it is checked first), but the `isEmpty` arm can then hide a block on an "editable" surface.
   * `getVisibleBlocks.spec.ts` pins that behaviour rather than leaving it undefined.
   */
  editable?: boolean
}

/**
 * Which blocks a surface renders, each paired with its unfiltered index.
 *
 * Two independent reasons to hide a block, with different gates:
 *
 * 1. **No v2 renderer.** Six catalog types have no entry in `../blocks/index.ts` — `cover`,
 *    `introduction`, `socials`, `sliderImage` and `benjamin-moore-main` (all v1-only) and `video`
 *    (catalogued but never built). `cover`/`introduction` are still authored by live code, so this
 *    is reachable. The editable canvas keeps them and `getBlock` draws a placeholder; customer
 *    view, print and preview drop them.
 * 2. **The block's own `isEmpty` says it has nothing to show.** Read-only surfaces only — an
 *    author editing an empty block has to be able to see it.
 *
 * Extracted from `Presentation.tsx` so the index arithmetic is directly testable: the editable
 * canvas filters nothing, so the off-by-one this guards against is not reachable through
 * `Presentation`'s own editor-only consumers any more (#22775).
 */
export function getVisibleBlocks(args: GetVisibleBlocksArgs): VisibleBlock[] {
  const { blocks, blockComponents, quote, options, estimator, isPrint, isSettings, editable } = args

  return (blocks ?? [])
    .map((block, blockIndex) => ({ block, blockIndex }))
    .filter(({ block }) => {
      const isEmpty = blockComponents?.[block.type]?.isEmpty
      if (!isEmpty) {
        return !!editable
      }

      if (!isSettings && isEmpty({ quote, options, block, isPrint, isSettings, estimator })) {
        return false
      }

      return true
    })
}
